import { addYears, subDays } from 'date-fns';
import type { FC } from 'react';
import { lazy } from 'react';
import { useParams } from 'react-router-dom';
import { useMemoOne } from 'use-memo-one';
import { useOptionalExchangeRatesCurrent } from 'src/apis/exchangerate-service/use-exchange-rates-current';
import type {
  ActivitiesParams,
  AuctionsParams,
  SurrendersParams,
  TradeProposalsParams,
} from 'src/apis/monolith/types';
import { useActivities } from 'src/apis/monolith/use-activities';
import { useAuctions } from 'src/apis/monolith/use-auctions';
import type { NetworkPointRequest } from 'src/apis/monolith/use-network-point';
import { useNwpCapacityGraph } from 'src/apis/monolith/use-nwp-capacity-graph';
import { useOptionalPlatformSettings } from 'src/apis/monolith/use-platform-settings';
import { useOptionalSurrenders } from 'src/apis/monolith/use-surrenders';
import { useTradeProposals } from 'src/apis/monolith/use-trade-proposals';
import {
  PageSpinner,
  SpinnerContainer,
} from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useNow } from 'src/hooks/use-now';
import { useNwpDetailParams } from 'src/pages/transport/network-points/details/point-details/use-page-params';

const Page = lazy(
  () => import('src/pages/transport/network-points/details/point-details/page')
);

type Props = {
  networkPoint: Successful<NetworkPointRequest>;
};

export const NetworkPointsDetails: FC<Props> = ({ networkPoint }) => {
  const pageParams = useNwpDetailParams();
  const { oldNetworkPointId } = useParams<{ oldNetworkPointId: string }>();

  const nwpCapacityGraph = useNwpCapacityGraph({
    oldNetworkPointId,
    networkPoint,
  });
  const now = useNow();
  const longTermParams = useMemoOne<AuctionsParams>(
    () => ({
      periodType: ['MONTH', 'QUARTER', 'YEAR', 'REST_OF_YEAR'],
      phase: ['PUBLISHED', 'OPEN', 'CLOSED'],
      networkPoint: [oldNetworkPointId],
      pageSize: pageParams.longTermAuctions.value.pageSize,
      start: pageParams.longTermAuctions.value.start,
      runtimeFrom: new Date(now).toISOString(),
      runtimeTo: addYears(now, 20).toISOString(),
    }),
    [
      oldNetworkPointId,
      pageParams.longTermAuctions.value.pageSize,
      pageParams.longTermAuctions.value.start,
    ]
  );
  const longTermAuctions = useAuctions({ params: longTermParams });

  const shortTermParams = useMemoOne<AuctionsParams>(
    () => ({
      periodType: ['DAY', 'WITHINDAY'],
      phase: ['OPEN'],
      networkPoint: [oldNetworkPointId],
      pageSize: pageParams.shortTermAuctions.value.pageSize,
      start: pageParams.shortTermAuctions.value.start,
      runtimeFrom: new Date(now).toISOString(),
      runtimeTo: addYears(now, 20).toISOString(),
    }),
    [
      oldNetworkPointId,
      pageParams.shortTermAuctions.value.pageSize,
      pageParams.shortTermAuctions.value.start,
    ]
  );
  const shortTermAuctions = useAuctions({ params: shortTermParams });

  const offeredProposalsParams = useMemoOne<TradeProposalsParams>(
    () =>
      ({
        proposalType: 'PROPOSAL_TO_SELL',
        networkPoint: [oldNetworkPointId],
        pageSize: pageParams.offeredProposals.value.pageSize,
        start: pageParams.offeredProposals.value.start,
      }) satisfies TradeProposalsParams,
    [
      oldNetworkPointId,
      pageParams.offeredProposals.value.pageSize,
      pageParams.offeredProposals.value.start,
    ]
  );
  const offeredProposals = useTradeProposals(offeredProposalsParams);

  const requestedProposalsParams = useMemoOne<TradeProposalsParams>(
    () =>
      ({
        proposalType: 'PROPOSAL_TO_BUY',
        networkPoint: [oldNetworkPointId],
        pageSize: pageParams.requestedProposals.value.pageSize,
        start: pageParams.requestedProposals.value.start,
      }) satisfies TradeProposalsParams,
    [
      oldNetworkPointId,
      pageParams.requestedProposals.value.pageSize,
      pageParams.requestedProposals.value.start,
    ]
  );
  const requestedProposals = useTradeProposals(requestedProposalsParams);

  const surrenderedCapacityParams = useMemoOne<SurrendersParams>(
    () => ({
      networkPoint: [oldNetworkPointId],
      pageSize: pageParams.surrenderedCapacity.value.pageSize,
      start: pageParams.surrenderedCapacity.value.start,
      sortColumn: pageParams.surrenderedCapacity.value.sortColumn,
      sortAscending:
        pageParams.surrenderedCapacity.value.sortAscending === 'asc',
    }),
    [
      oldNetworkPointId,
      pageParams.surrenderedCapacity.value.pageSize,
      pageParams.surrenderedCapacity.value.start,
      pageParams.surrenderedCapacity.value.sortColumn,
      pageParams.surrenderedCapacity.value.sortAscending,
    ]
  );
  const surrenderedCapacity = useOptionalSurrenders({
    params: surrenderedCapacityParams,
  });

  const exchangeRates = useOptionalExchangeRatesCurrent();

  const platformSettings = useOptionalPlatformSettings();

  const activities = useActivities({
    params: useMemoOne<ActivitiesParams>(
      () => ({
        networkPoint: [networkPoint.response.data.id],
        from: subDays(now, 30).toISOString(),
        sortBy: 'lastActivity',
      }),
      [networkPoint.response.data.id, now]
    ),
    onlyIfAuthenticated: true,
  });

  if (
    nwpCapacityGraph?.response === null ||
    !longTermAuctions.response ||
    !shortTermAuctions.response ||
    !offeredProposals.response ||
    !requestedProposals.response ||
    surrenderedCapacity?.response === null ||
    !exchangeRates.loaded ||
    activities?.response === null ||
    platformSettings?.loaded === false
  )
    return <PageSpinner />;

  return (
    <SpinnerContainer
      pending={
        networkPoint.pending ||
        nwpCapacityGraph?.pending ||
        surrenderedCapacity?.pending ||
        longTermAuctions.pending ||
        shortTermAuctions.pending ||
        offeredProposals.pending ||
        requestedProposals.pending ||
        Boolean(activities?.pending)
      }
    >
      <Page
        pageParams={pageParams}
        networkPoint={networkPoint}
        nwpCapacityGraph={nwpCapacityGraph}
        longTermAuctions={longTermAuctions}
        shortTermAuctions={shortTermAuctions}
        offeredProposals={offeredProposals}
        requestedProposals={requestedProposals}
        surrenderedCapacity={surrenderedCapacity}
        exchangeRates={exchangeRates}
        activities={activities}
        platformSettings={platformSettings?.response?.data}
      />
    </SpinnerContainer>
  );
};
